import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { useSpaceStore } from "../../stores";
import { GalaxyView } from "./GalaxyView";
import { SystemView } from "./SystemView";
import "./SpaceCanvas.css";

function ViewOverlays({ mode }: { mode: "galaxy" | "system" }): React.JSX.Element {
	const galaxyScale = useSpaceStore((s) => s.galaxyView.scale);
	const systemScale = useSpaceStore((s) => s.systemView.scale);
	const systemCount = useSpaceStore((s) => s.systems.length);
	const focusedSystem = useSpaceStore((s) => s.focusedSystem);

	const scale = mode === "galaxy" ? galaxyScale : systemScale;
	const planetCount = focusedSystem?.planets.length ?? 0;

	return (
		<>
			<div className="zoom-indicator">
				<span className="zoom-label">Zoom</span>
				<span className="zoom-value">{(scale * 100).toFixed(0)}%</span>
			</div>
			<div className="object-count">
				{mode === "galaxy" ? (
					<span>&#x229B; {systemCount} systems</span>
				) : (
					<span>{planetCount} planets</span>
				)}
			</div>
		</>
	);
}

export function SpaceCanvas(): React.JSX.Element {
	const viewMode = useSpaceStore((s) => s.viewMode);
	const [transitioning, setTransitioning] = useState(false);
	const [displayMode, setDisplayMode] = useState(viewMode);
	const { isMobile } = useDeviceCapabilities();

	// Handle view transitions via CSS overlay fade
	const prevModeRef = useSpaceStore((s) => s.viewMode);
	if (prevModeRef !== displayMode && !transitioning) {
		setTransitioning(true);
		// Brief dark overlay, then swap content
		setTimeout(() => {
			setDisplayMode(prevModeRef);
			setTimeout(() => setTransitioning(false), 200);
		}, 150);
	}

	return (
		<div className="space-canvas-container">
			<Canvas
				orthographic
				dpr={[1, 1.5]}
				gl={{ alpha: false }}
				camera={{ position: [0, 0, 100], zoom: 1, near: 0.1, far: 1000 }}
				scene={{ background: new THREE.Color("#060d1e") }}
				style={{ position: "absolute", inset: 0 }}
			>
				{displayMode === "galaxy" ? <GalaxyView /> : <SystemView />}
			</Canvas>

			{/* HTML overlays — outside Canvas, in normal React DOM */}
			{displayMode === "galaxy" && <div className="galaxy-tooltip" style={{ display: "none" }} />}

			{/* Mobile crosshair indicator (galaxy view only) */}
			{isMobile && displayMode === "galaxy" && (
				<div
					style={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						pointerEvents: "none",
						zIndex: 5,
					}}
				>
					<svg width="24" height="24" viewBox="0 0 24 24">
						<circle
							cx="12"
							cy="12"
							r="8"
							fill="none"
							stroke="rgba(255, 255, 255, 0.3)"
							strokeWidth="1"
						/>
						<line x1="12" y1="2" x2="12" y2="6" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="1" />
						<line x1="12" y1="18" x2="12" y2="22" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="1" />
						<line x1="2" y1="12" x2="6" y2="12" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="1" />
						<line x1="18" y1="12" x2="22" y2="12" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="1" />
					</svg>
				</div>
			)}

			<ViewOverlays mode={displayMode} />

			{/* Transition overlay */}
			<AnimatePresence>
				{transitioning && (
					<motion.div
						key="transition-overlay"
						initial={{ opacity: 0 }}
						animate={{ opacity: 0.7 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
						style={{
							position: "absolute",
							inset: 0,
							background: "#060d1e",
							pointerEvents: "none",
							zIndex: 10,
						}}
					/>
				)}
			</AnimatePresence>
		</div>
	);
}
